import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Save, Zap, RefreshCw, Clock, Key, AlertTriangle, CheckCircle } from 'lucide-react';
import { AdminAutomationDPISettings } from './AdminAutomationDPISettings';
import { AdminEtsyDriveSettings } from './AdminEtsyDriveSettings';

interface ApiKeyStats {
  activeKeys: number;
  totalRpm: number;
  totalDailyCapacity: number;
}

interface TodayUsage {
  totalImagesGenerated: number;
  automationImages: number;
  dashboardImages: number;
  guestImages: number;
}

export const AdminSiteConfigLimitsSettings = () => {
  const [dailyLimit, setDailyLimit] = useState('300');
  const [batchSize, setBatchSize] = useState('1');
  const [maxRequests, setMaxRequests] = useState('10');
  const [requestDelay, setRequestDelay] = useState('100');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [lastReset, setLastReset] = useState<string | null>(null);
  const [apiKeyStats, setApiKeyStats] = useState<ApiKeyStats>({ activeKeys: 0, totalRpm: 0, totalDailyCapacity: 0 });
  const [todayUsage, setTodayUsage] = useState<TodayUsage>({ totalImagesGenerated: 0, automationImages: 0, dashboardImages: 0, guestImages: 0 });
  const [refreshingStats, setRefreshingStats] = useState(false);
  const [quotaStatus, setQuotaStatus] = useState<'ok' | 'warning' | 'exhausted'>('ok');

  useEffect(() => {
    fetchLimitConfig();
    fetchLastResetTime();
    fetchApiKeyStats();
    fetchTodayUsage();
  }, []);

  const fetchApiKeyStats = async () => {
    setRefreshingStats(true);
    try {
      const { data, error } = await supabase
        .from('api_key_pool')
        .select('rate_limit_per_minute, daily_limit, is_active')
        .eq('is_active', true);
      
      if (error) throw error;
      
      if (data) {
        setApiKeyStats({
          activeKeys: data.length,
          totalRpm: data.reduce((sum, key) => sum + (key.rate_limit_per_minute || 5), 0),
          totalDailyCapacity: data.reduce((sum, key) => sum + (key.daily_limit || 300), 0)
        });
      }
    } catch (error) {
      console.error('Error fetching API key stats:', error);
    } finally {
      setRefreshingStats(false);
    }
  };

  const fetchTodayUsage = async () => {
    try {
      // Get today's start (midnight UTC)
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      const todayStart = today.toISOString();

      const { data, error } = await supabase
        .from('generated_images')
        .select('generation_type')
        .gte('created_at', todayStart);

      if (error) throw error;

      if (data) {
        const automation = data.filter(img => img.generation_type === 'automation').length;
        const dashboard = data.filter(img => img.generation_type === 'dashboard' || img.generation_type === 'manual').length;
        const guest = data.filter(img => img.generation_type === 'guest').length;
        const total = data.length;

        setTodayUsage({
          totalImagesGenerated: total,
          automationImages: automation,
          dashboardImages: dashboard,
          guestImages: guest
        });

        // Update quota status based on usage vs capacity
        const usagePercent = (total / apiKeyStats.totalDailyCapacity) * 100;
        if (usagePercent >= 100 || total >= apiKeyStats.totalDailyCapacity) {
          setQuotaStatus('exhausted');
        } else if (usagePercent >= 80) {
          setQuotaStatus('warning');
        } else {
          setQuotaStatus('ok');
        }
      }
    } catch (error) {
      console.error('Error fetching today usage:', error);
    }
  };

  const fetchLimitConfig = async () => {
    try {
      const { data: configs, error } = await supabase
        .from('site_configurations')
        .select('key, value')
        .in('key', [
          'daily_generation_limit',
          'automation_default_batch_size',
          'automation_max_requests_per_batch',
          'automation_request_delay_ms'
        ]);

      if (error) throw error;
      
      if (configs) {
        for (const config of configs) {
          if (config.key === 'daily_generation_limit') setDailyLimit(config.value);
          if (config.key === 'automation_default_batch_size') setBatchSize(config.value);
          if (config.key === 'automation_max_requests_per_batch') setMaxRequests(config.value);
          if (config.key === 'automation_request_delay_ms') setRequestDelay(config.value);
        }
      }
    } catch (error) {
      console.error('Error fetching limit config:', error);
      toast.error('Failed to load limit configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchLastResetTime = async () => {
    try {
      const { data } = await supabase
        .from('api_key_pool')
        .select('last_reset')
        .order('last_reset', { ascending: false })
        .limit(1)
        .single();
      
      if (data?.last_reset) {
        setLastReset(data.last_reset);
      }
    } catch (error) {
      console.error('Error fetching last reset time:', error);
    }
  };

  const handleResetApiKeys = async () => {
    setResetting(true);
    try {
      const { data, error } = await supabase.functions.invoke('reset-api-key-daily-limits', {
        body: {}
      });

      if (error) throw error;
      
      toast.success(`Reset daily limits for ${data.reset_count} API keys`);
      await fetchLastResetTime();
    } catch (error) {
      console.error('Error resetting API keys:', error);
      toast.error('Failed to reset API key limits');
    } finally {
      setResetting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const limitValue = parseInt(dailyLimit);
      const batchValue = parseInt(batchSize);
      const maxRequestsValue = parseInt(maxRequests);
      const delayValue = parseInt(requestDelay);
      
      if (isNaN(limitValue) || limitValue < 1) {
        toast.error('Please enter a valid daily limit greater than 0');
        setSaving(false);
        return;
      }
      
      if (isNaN(batchValue) || batchValue < 1 || batchValue > 4) {
        toast.error('Batch size (n) must be between 1 and 4 to avoid API errors');
        setSaving(false);
        return;
      }

      if (isNaN(maxRequestsValue) || maxRequestsValue < 5 || maxRequestsValue > 50) {
        toast.error('Max requests per batch must be between 5 and 50');
        setSaving(false);
        return;
      }

      if (isNaN(delayValue) || delayValue < 0 || delayValue > 500) {
        toast.error('Request delay must be between 0 and 500ms');
        setSaving(false);
        return;
      }

      const updates = [
        { key: 'daily_generation_limit', value: dailyLimit },
        { key: 'automation_default_batch_size', value: batchSize },
        { key: 'automation_max_requests_per_batch', value: maxRequests },
        { key: 'automation_request_delay_ms', value: requestDelay },
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('site_configurations')
          .update({ 
            value: update.value, 
            updated_at: new Date().toISOString() 
          })
          .eq('key', update.key);
        
        if (error) throw error;
      }

      toast.success('Limit settings updated successfully');
      await fetchLimitConfig();
    } catch (error) {
      console.error('Error saving limits:', error);
      toast.error('Failed to update limits');
    } finally {
      setSaving(false);
    }
  };

  const formatLastReset = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Calculate expected throughput using actual API key stats
  const batchValue = parseInt(batchSize) || 1;
  const maxRequestsValue = parseInt(maxRequests) || 10;
  const expectedImagesPerMinute = apiKeyStats.totalRpm * batchValue;
  const maxImagesPerBatch = maxRequestsValue * batchValue;
  const usagePercent = apiKeyStats.totalDailyCapacity > 0 
    ? Math.round((todayUsage.totalImagesGenerated / apiKeyStats.totalDailyCapacity) * 100) 
    : 0;

  // Calculate time until A4F quota reset (midnight UTC)
  const now = new Date();
  const midnightUTC = new Date(now);
  midnightUTC.setUTCHours(24, 0, 0, 0);
  const hoursUntilReset = Math.ceil((midnightUTC.getTime() - now.getTime()) / (1000 * 60 * 60));
  const minutesUntilReset = Math.ceil((midnightUTC.getTime() - now.getTime()) / (1000 * 60)) % 60;

  return (
    <div className="space-y-6">
      {/* Today's A4F Quota Status */}
      <Card className={`backdrop-blur-md border ${
        quotaStatus === 'exhausted' ? 'bg-red-500/10 border-red-500/30' :
        quotaStatus === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
        'bg-blue-500/10 border-blue-500/30'
      }`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {quotaStatus === 'exhausted' ? (
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                ) : quotaStatus === 'warning' ? (
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                )}
                <h4 className={`font-semibold ${
                  quotaStatus === 'exhausted' ? 'text-red-400' :
                  quotaStatus === 'warning' ? 'text-yellow-400' :
                  'text-blue-400'
                }`}>
                  Today's A4F Quota Status
                </h4>
                <Badge variant={quotaStatus === 'exhausted' ? 'destructive' : quotaStatus === 'warning' ? 'secondary' : 'default'}>
                  {usagePercent}% used
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { fetchTodayUsage(); fetchApiKeyStats(); }}
                  disabled={refreshingStats}
                  className="h-6 w-6 p-0"
                >
                  <RefreshCw className={`h-3 w-3 ${refreshingStats ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Today:</span>
                  <span className="ml-2 font-bold text-foreground">{todayUsage.totalImagesGenerated.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Automation:</span>
                  <span className="ml-2 font-medium text-purple-400">{todayUsage.automationImages.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Dashboard:</span>
                  <span className="ml-2 font-medium text-blue-400">{todayUsage.dashboardImages.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Guest:</span>
                  <span className="ml-2 font-medium text-green-400">{todayUsage.guestImages.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="text-right space-y-1">
              <div className="text-xs text-muted-foreground">
                Remaining: <span className={`font-bold ${quotaStatus === 'exhausted' ? 'text-red-400' : 'text-foreground'}`}>
                  {Math.max(0, apiKeyStats.totalDailyCapacity - todayUsage.totalImagesGenerated).toLocaleString()}
                </span> images
              </div>
              <div className="text-xs text-muted-foreground">
                A4F Reset: <span className="font-medium text-foreground">{hoursUntilReset}h {minutesUntilReset}m</span> (Midnight UTC)
              </div>
              {quotaStatus === 'exhausted' && (
                <p className="text-xs text-red-400 font-medium">
                  ⚠️ Quota exhausted! Generation blocked until reset.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Throughput Calculator */}
      <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-md border-green-500/30">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-green-400">Expected Throughput</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchApiKeyStats}
                  disabled={refreshingStats}
                  className="h-6 w-6 p-0"
                >
                  <RefreshCw className={`h-3 w-3 ${refreshingStats ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                <span className="text-primary font-medium">{apiKeyStats.activeKeys}</span> API keys × 
                <span className="text-primary font-medium"> {apiKeyStats.totalRpm}</span> total RPM × 
                <span className="text-primary font-medium"> {batchValue}</span> images = 
                <span className="text-green-400 font-bold"> {expectedImagesPerMinute} images/min</span>
              </p>
            </div>
            <div className="text-right space-y-1">
              <div className="flex items-center gap-2 justify-end text-xs text-muted-foreground">
                <Key className="h-3 w-3" />
                <span>Daily capacity: {apiKeyStats.totalDailyCapacity.toLocaleString()} images</span>
              </div>
              <p className="text-xs text-muted-foreground">Max per batch: {maxImagesPerBatch} images</p>
              <p className="text-xs text-muted-foreground">Request delay: {requestDelay}ms</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Generation Limits & Speed
          </CardTitle>
          <CardDescription>
            Configure generation limits and automation speed settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="daily-limit">Daily Generation Limit</Label>
            <Input
              id="daily-limit"
              type="number"
              min="1"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
              placeholder="300"
            />
            <p className="text-sm text-muted-foreground">
              Maximum number of images a user can generate per day
            </p>
          </div>

          <div className="border-t border-white/10 pt-4">
            <h4 className="font-medium mb-4 text-sm text-muted-foreground uppercase tracking-wide">
              Automation Speed Settings
            </h4>
            
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="batch-size">Images Per Request (n)</Label>
                <Input
                  id="batch-size"
                  type="number"
                  min="1"
                  max="4"
                  value={batchSize}
                  onChange={(e) => setBatchSize(e.target.value)}
                  placeholder="2"
                />
                <p className="text-xs text-muted-foreground">
                  1 = most reliable, 2+ = faster but may hit rate limits
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="max-requests">Max Requests/Batch</Label>
                <Input
                  id="max-requests"
                  type="number"
                  min="5"
                  max="50"
                  value={maxRequests}
                  onChange={(e) => setMaxRequests(e.target.value)}
                  placeholder="10"
                />
                <p className="text-xs text-muted-foreground">
                  10-20 recommended to avoid CPU timeout
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="request-delay">Request Delay (ms)</Label>
                <Input
                  id="request-delay"
                  type="number"
                  min="0"
                  max="500"
                  value={requestDelay}
                  onChange={(e) => setRequestDelay(e.target.value)}
                  placeholder="100"
                />
                <p className="text-xs text-muted-foreground">
                  100ms = sequential, 0 = parallel
                </p>
              </div>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            API Key Daily Reset
          </CardTitle>
          <CardDescription>
            Reset all API key daily limits. Auto-resets daily at midnight UTC (5:30 AM IST).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Last reset: {formatLastReset(lastReset)}</span>
          </div>
          
          <Button
            onClick={handleResetApiKeys}
            disabled={resetting}
            variant="outline"
            className="w-full sm:w-auto"
          >
            {resetting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset All API Key Limits Now
              </>
            )}
          </Button>
          
          <p className="text-xs text-muted-foreground">
            This resets daily_images_generated, requests_this_minute, and current_usage for all API keys.
          </p>
        </CardContent>
      </Card>
      {/* Image Enhancement Settings */}
      <AdminAutomationDPISettings />
      
      {/* Etsy & Drive Integration Settings */}
      <AdminEtsyDriveSettings />
    </div>
  );
};